/* ============================================================================
   Auth module — change-password dialog.
   Lets the signed-in user rotate their own password: current + new + confirm.
   Client-side pre-checks (non-empty, new === confirm, min-length hint) catch
   the obvious cases; the server enforces the live password policy and returns
   the authoritative error (e.g. "Current password is incorrect.").
   Reuses the admin Modal shell and the shared field/error design classes.
   ============================================================================ */

import { useState } from 'react';
import { apiPost, isApiError } from '../../lib/api';
import Modal from '../admin/Modal';

interface Props {
  onClose: () => void;
}

/** Soft client-side floor. The server enforces the real (DB-driven) policy. */
const MIN_LENGTH = 10;

type Status = 'idle' | 'busy' | 'error' | 'success';

export default function ChangePasswordDialog({ onClose }: Props) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const busy = status === 'busy';
  const done = status === 'success';

  async function submit() {
    setError(null);

    if (!current || !next || !confirm) {
      setStatus('error');
      setError('All three fields are required.');
      return;
    }
    if (next.length < MIN_LENGTH) {
      setStatus('error');
      setError(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setStatus('error');
      setError('New password and confirmation do not match.');
      return;
    }
    if (next === current) {
      setStatus('error');
      setError('New password must be different from your current password.');
      return;
    }

    setStatus('busy');
    try {
      await apiPost('/api/auth/change-password', {
        currentPassword: current,
        newPassword: next,
      });
      setStatus('success');
      // Brief confirmation, then close.
      window.setTimeout(onClose, 1100);
    } catch (e) {
      setStatus('error');
      setError(isApiError(e) ? e.message : 'Could not change your password.');
    }
  }

  // --- Success state. ------------------------------------------------------
  if (done) {
    return (
      <Modal
        title="Password changed"
        onClose={onClose}
        footer={
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <p style={{ marginTop: 0 }}>
          Your password has been updated. Use it the next time you sign in.
        </p>
      </Modal>
    );
  }

  // --- Form state. ---------------------------------------------------------
  return (
    <Modal
      title="Change password"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Update password'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void submit();
        }}
      >
        {error && (
          <div className="error-text" role="alert" style={{ marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div className="field">
          <label htmlFor="cp-current">Current password</label>
          <input
            id="cp-current"
            className="in"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoFocus
          />
        </div>

        <div className="field">
          <label htmlFor="cp-new">New password</label>
          <input
            id="cp-new"
            className="in"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <span className="hint">Use at least {MIN_LENGTH} characters.</span>
        </div>

        <div className="field" style={{ marginBottom: 4 }}>
          <label htmlFor="cp-confirm">Confirm new password</label>
          <input
            id="cp-confirm"
            className="in"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {/* Submit on Enter without showing a second button. */}
        <button type="submit" style={{ display: 'none' }} aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
